import { useCallback, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const buildSessionIssue = (redirectTo) => ({
  title: 'Session Expired',
  message: 'Your session has expired. Please log in again to continue.',
  actionLabel: 'Go To Login',
  actionTo: redirectTo,
  autoRedirectTo: redirectTo,
  autoRedirectDelayMs: 2500,
});

const buildDeniedIssue = ({ title, message, redirectTo, actionLabel }) => ({
  title,
  message,
  actionLabel,
  actionTo: redirectTo,
  autoRedirectTo: redirectTo,
  autoRedirectDelayMs: 2500,
});

const usePageAccessRedirect = ({
  deniedTitle = 'Access Denied',
  deniedMessage = 'You do not have permission to access this area.',
  deniedRedirectTo = '/',
  deniedActionLabel = 'Go To Home',
  sessionRedirectTo = '/login',
} = {}) => {
  const [accessIssue, setAccessIssue] = useState(null);
  const { logout } = useAuth();
  const hasHandledSessionExpiredRef = useRef(false);

  const clearAccessIssue = useCallback(() => {
    setAccessIssue(null);
    hasHandledSessionExpiredRef.current = false;
  }, []);

  const handleAccessError = useCallback(
    (error, overrides = {}) => {
      const status = error && error.response ? error.response.status : null;

      if (status === 401) {
        if (!hasHandledSessionExpiredRef.current) {
          hasHandledSessionExpiredRef.current = true;
          Promise.resolve(logout()).catch(() => {});
        }
        setAccessIssue(buildSessionIssue(overrides.sessionRedirectTo || sessionRedirectTo));
        return true;
      }

      if (status === 403) {
        setAccessIssue(
          buildDeniedIssue({
            title: overrides.deniedTitle || deniedTitle,
            message: overrides.deniedMessage || deniedMessage,
            redirectTo: overrides.deniedRedirectTo || deniedRedirectTo,
            actionLabel: overrides.deniedActionLabel || deniedActionLabel,
          })
        );
        return true;
      }

      return false;
    },
    [deniedActionLabel, deniedMessage, deniedRedirectTo, deniedTitle, logout, sessionRedirectTo]
  );

  return {
    accessIssue,
    clearAccessIssue,
    handleAccessError,
  };
};

export default usePageAccessRedirect;